import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Skeleton,
} from '@mui/material';
import { Users, Plus, MoreVertical, Check, UserMinus } from 'lucide-react';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import {
  fetchWorkspaces,
  fetchWorkspace,
  removeWorkspaceMember,
  updateMemberRole,
} from '../../store/workspaceSlice';
import { showToast } from '../../store/uiSlice';
import { WorkspaceMember, WorkspaceMemberRole } from '../../types/common';
import { usePermissions } from '../../hooks/usePermissions';
import AddMemberModal from '../../components/Workspace/AddMemberModal';
import { StatCardSkeleton } from '../../components/common/Skeletons';
import './Team.scss';

const ROLE_OPTIONS: WorkspaceMemberRole[] = [
  WorkspaceMemberRole.VIEWER,
  WorkspaceMemberRole.MEMBER,
  WorkspaceMemberRole.ADMIN,
];

const roleLabel = (role: WorkspaceMemberRole) =>
  role.charAt(0).toUpperCase() + role.slice(1);

const Team: React.FC = () => {
  const dispatch = useAppDispatch();
  const { workspaces, currentWorkspace, loading, error } = useAppSelector(
    (state) => state.workspaces
  );

  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [manageAnchorEl, setManageAnchorEl] = useState<null | HTMLElement>(null);
  const [managingMember, setManagingMember] = useState<WorkspaceMember | null>(null);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

  useEffect(() => {
    // Always refetch on mount rather than trusting redux-persist's cached
    // list — workspaces/currentWorkspace survive logout (see authSlice
    // logout, which only clears the token) and across accounts sharing a
    // browser, so a stale membership snapshot would otherwise never refresh.
    dispatch(fetchWorkspaces());
  }, [dispatch]);

  const firstWorkspaceId = workspaces[0]?.id;

  useEffect(() => {
    // The list endpoint (fetchWorkspaces) auto-selects currentWorkspace but
    // never includes .members — only the detail endpoint does. Always fetch
    // detail for the target workspace on mount/id-change instead of gating
    // on whether .members already looks populated, since that could be a
    // stale persisted snapshot rather than freshly-loaded data.
    // Depends on firstWorkspaceId, not the whole `workspaces` array - that
    // array gets a new reference on every fetchWorkspaces() resolution,
    // which would otherwise refetch in a loop.
    const targetId = currentWorkspace?.id || firstWorkspaceId;
    if (targetId) {
      dispatch(fetchWorkspace(targetId));
    }
  }, [currentWorkspace?.id, firstWorkspaceId, dispatch]);

  const { isOwnerOrAdmin: canManage } = usePermissions(currentWorkspace?.id);

  const handleOpenManage = (event: React.MouseEvent<HTMLElement>, member: WorkspaceMember) => {
    setManageAnchorEl(event.currentTarget);
    setManagingMember(member);
  };

  const handleCloseManage = () => {
    setManageAnchorEl(null);
    setManagingMember(null);
  };

  const handleChangeRole = async (role: WorkspaceMemberRole) => {
    if (!currentWorkspace || !managingMember) return;
    const member = managingMember;
    handleCloseManage();

    if (member.role === role) return;

    try {
      await dispatch(
        updateMemberRole({
          workspaceId: currentWorkspace.id,
          userId: member.userId,
          role,
        })
      ).unwrap();
      dispatch(showToast({ message: 'Member role updated', type: 'success' }));
    } catch (err: any) {
      dispatch(
        showToast({
          message: (typeof err === 'string' ? err : err?.message) || 'Failed to update role',
          type: 'error',
        })
      );
    }
  };

  const handleRequestRemove = () => {
    setManageAnchorEl(null);
    setRemoveConfirmOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!currentWorkspace || !managingMember) return;
    const member = managingMember;
    setRemoveConfirmOpen(false);
    setManagingMember(null);

    try {
      await dispatch(
        removeWorkspaceMember({ workspaceId: currentWorkspace.id, userId: member.userId })
      ).unwrap();
      dispatch(showToast({ message: 'Member removed', type: 'success' }));
    } catch (err: any) {
      dispatch(
        showToast({
          message: (typeof err === 'string' ? err : err?.message) || 'Failed to remove member',
          type: 'error',
        })
      );
    }
  };

  const handleRetry = () => {
    dispatch(fetchWorkspaces());
  };

  // Initial load, or workspace resolved from the list endpoint but its full
  // detail (which carries .members) hasn't come back yet
  if ((loading && !currentWorkspace) || (currentWorkspace && !currentWorkspace.members)) {
    return (
      <Box className="team-page">
        <Box className="team-page__header">
          <Box>
            <Skeleton variant="text" width={160} height={40} />
            <Skeleton variant="text" width={260} height={20} />
          </Box>
        </Box>
        <Box className="team-page__content">
          {Array.from({ length: 3 }).map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </Box>
      </Box>
    );
  }

  // Initial load failed
  if (error && !currentWorkspace) {
    return (
      <Box className="team-page">
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Couldn't load your team
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button variant="outlined" onClick={handleRetry}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  // No workspace to show a team for
  if (!currentWorkspace) {
    return (
      <Box className="team-page">
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Users size={48} strokeWidth={1} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            No workspace yet
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Create a workspace to start building your team
          </Typography>
        </Box>
      </Box>
    );
  }

  const teamMembers = (currentWorkspace.members || []).filter(Boolean);
  const isEmptyTeam = teamMembers.length <= 1;

  return (
    <Box className="team-page">
      <Box className="team-page__header">
        <Box>
          <Typography variant="h4" className="team-page__title">
            My Team
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your team members and their roles
          </Typography>
        </Box>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            className="team-page__add-btn"
            onClick={() => setAddMemberModalOpen(true)}
          >
            Invite Member
          </Button>
        )}
      </Box>

      {isEmptyTeam ? (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Users size={48} strokeWidth={1} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            No team members yet
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {canManage
              ? 'Invite teammates to start collaborating'
              : 'No members have joined this workspace yet'}
          </Typography>
        </Box>
      ) : (
        <Box className="team-page__content">
          {teamMembers.map((member, i) => {
            const displayName = member.user?.name || member.user?.email || `User ${member.userId}`;
            const displayEmail = member.user?.email || '';
            const isMemberOwner = member.role === WorkspaceMemberRole.OWNER;

            return (
              <Card
                key={member.id}
                className="team-member-card stagger-item"
                style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }}
              >
                <CardContent>
                  <Box className="team-member-card__content">
                    <Box className="team-member-card__info">
                      <Avatar
                        className="team-member-card__avatar"
                        src={member.user?.avatar}
                        sx={{ width: 56, height: 56 }}
                      >
                        {displayName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box className="team-member-card__details">
                        <Typography variant="h6" className="team-member-card__name">
                          {displayName}
                        </Typography>
                        {displayEmail && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            className="team-member-card__email"
                          >
                            {displayEmail}
                          </Typography>
                        )}
                        <Box className="team-member-card__badges">
                          <Chip
                            label={roleLabel(member.role)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </Box>
                    {canManage && !isMemberOwner && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenManage(e, member)}
                        aria-label="Manage member"
                      >
                        <MoreVertical size={18} />
                      </IconButton>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      <Menu anchorEl={manageAnchorEl} open={Boolean(manageAnchorEl)} onClose={handleCloseManage}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Change role
          </Typography>
        </Box>
        {ROLE_OPTIONS.map((role) => (
          <MenuItem key={role} onClick={() => handleChangeRole(role)}>
            <ListItemIcon>
              {managingMember?.role === role ? <Check size={16} /> : null}
            </ListItemIcon>
            <ListItemText>{roleLabel(role)}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={handleRequestRemove} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <UserMinus size={16} color="currentColor" />
          </ListItemIcon>
          <ListItemText>Remove from workspace</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={removeConfirmOpen} onClose={() => setRemoveConfirmOpen(false)}>
        <DialogTitle>Remove member?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {managingMember?.user?.name || managingMember?.user?.email || 'This member'} will lose
            access to this workspace immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmRemove}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <AddMemberModal
        open={addMemberModalOpen}
        onClose={() => setAddMemberModalOpen(false)}
        workspaceId={currentWorkspace.id}
      />
    </Box>
  );
};

export default Team;
